import pLimit from "p-limit";

export class UploadError extends Error {
    constructor(message, e) {
        super(message);
        this.name = 'UploadError';
        this.cause = e;
    }
}

const DefaultProps = {
    shouldUseMultipart: (fileSize) => fileSize >= 10 * 2 ** 20,
    allowEmpty: false,
    maxConcurrentUploads: 5,
    request: fetch
}

const throwIfFalse = (condition, message) => {
    if (!condition) {
        throw new UploadError(message);
    }
};

const withRetry = (fn, retries = 3, delay = 1000, backoffFactor = 2) => async (...args) => {
	for (let i = 0; i < retries; i++) {
		try {
			return await fn(...args);
		} catch (e) {
			if (i === retries - 1) {
				throw new Error(
                    `Function failed after ${retries} retries. Last error: ${e.message}`
				);
			}
			const currentDelay = delay * (backoffFactor ** i);
			await new Promise(resolve => setTimeout(resolve, currentDelay));
		}
	}
};

/**
 * Creates and returns an upload service.
 * 
 * @param {Object} [props] - Configuration properties for the upload service.
 * @param {function(number): boolean} [props.shouldUseMultipart] - Determines whether to use multipart upload based on the file size.
 * @param {boolean} [props.allowEmpty] - Indicates whether empty files are allowed.
 * @param {number} [props.maxConcurrentUploads] - Maximum concurrent uploads allowed.
 * @param {function} [props.request] - The request function, defaults to `fetch`.
 * 
 * @returns {Object} 
 * @property {function(UUID): Promise<string>} downloadUrl - Fetches the download URL for a given file ID.
 * @property {function(UUID): Promise<File>} download - Downloads the file for a given file ID.
 * @property {function(File, function): Promise<Object>} upload - Uploads the file and reports progress.
 */
const createUploadService = (props) => {
    const options = { ...DefaultProps, ...(props || {}) };

    const limit = pLimit(options.maxConcurrentUploads);
    const calcNumberOfParts = (fileSize) => Math.floor(fileSize / (5 * 2 ** 20));

    const createMultipartPresignedUrls = async (fileName, fileSize) => {
        const numberOfParts = calcNumberOfParts(fileSize);

        const response = await options.request("/api/v2/attachments/multipart/upload", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileName, fileSize, numberOfParts })
        });

        return await response.json();
    };

    const downloadUrl = async (fileId) => {
        const response = await options.request(`api/v2/attachments/download?uploadMetadataId=${fileId}`);
        return response.text();
    }

    const download = async (fileId) => {
        const url = await downloadUrl(fileId);
        // get filename from url
        // Split the URL by "/" and get the last element using spread operator
        const [...parts] = url.split('/');
        // get the filename
        const fileName = parts.pop();

        const response = await options.request(url);
        const blob = await response.blob();

        return new File([blob], fileName);
    }

    const createSinglePresignedUrl = async (fileName, fileSize) => {

        const response = await options.request("/api/v2/attachments/upload", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileName, fileSize })
        });

        return await response.json();
    }

    const preparePart = (blob, partNumber, partSize) => {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, blob.size);
        return blob.slice(start, end);
    }

    const uploadPart = async (uploadUrl, part) => {
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: part
        });
        return JSON.parse(response.headers.get('ETag'));
    };

    const uploadPartWithRetry = withRetry(uploadPart, 3, 2000);

    const completeUpload = async (uploadData, etags) => {
        if (etags && etags.length > 1) {
            const response = await options.request(`/api/v2/attachments/multipart/upload/${uploadData.uploadMetadataId}/complete`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ uploadId: uploadData.uploadId, etags })
            });
            return await response.json();
        }
        else {
            const response = await options.request(`/api/v2/attachments/upload/${uploadData.uploadMetadataId}/complete`, {
                method: "POST"
            });
            return await response.json();
        }

    };

    const createPresignedUrls = async (file) => {
        if (options.shouldUseMultipart(file.size)) {
            return await createMultipartPresignedUrls(file.name, file.size);
        }
        else {
            const data = await createSinglePresignedUrl(file.name, file.size);
            return {
                uploadMetadataId: data.uploadMetadataId,
                uploadUrls: [data.preSignedUrl]
            };
        }
    }

    const createState = (initialState, onStateChange) => {
        const state = {};

        const setState = (newState) => {
            Object.assign(state, newState);
            if (typeof onStateChange === 'function') {
                onStateChange({ ...state });
            }
        };

        setState(initialState); // initial state
        return [state, setState];
    };


    const uploadFile = async (file, onProgress) => {
        const initialState = { uploaded: 0, totalSize: file.size, status: "ready" };
        const [state, setState] = createState(initialState, onProgress);

        // TODO: validate everything

        setState({ status: "preparing" });
        const uploadData = await createPresignedUrls(file);
        setState({ status: "uploading" });

        const numberOfParts = uploadData.uploadUrls.length;
        const partSize = Math.ceil(file.size / numberOfParts);

        const queuedParts = uploadData.uploadUrls.map((uploadUrl, index) => limit(async () => {
            const partNumber = index + 1;
            const part = preparePart(file, partNumber, partSize);
            const etagId = await uploadPartWithRetry(uploadUrl, part);
            setState({
                uploaded: Math.min(state.uploaded + partSize, state.totalSize)
            });
            return { partNumber, etagId };
        }));

        try {
            const parts = await Promise.all(queuedParts);

            // complete upload
            setState({ status: "finalizing" });
            const fileMetadata = await completeUpload(uploadData, parts);
            setState({ status: "complete" });

            return fileMetadata;
        }
        catch (e) {
            setState({ status: "error", error: new UploadError(e.message, e) });
        }
    }

    const upload = (file, onProgress) => {
        throwIfFalse(file.name.length > 0, 'File name must not be blank');
        throwIfFalse(options.allowEmpty || file.size > 0, 'File must not be blank');


        return uploadFile(file, onProgress);
    };

    return {
        downloadUrl,
        download,
        upload
    }
}


export const attachments = createUploadService({
    request: (url, options) => {
        const storedToken = localStorage?.getItem("fk_accessToken");
        
        return fetch(url, {
            ...options,
            headers: {
                ...(options.headers ?? {}),
                "Authorization": `Bearer ${storedToken}`
            }
        });
    }
});
